import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Crown, LayoutDashboard, Users, Building2, Beaker, Receipt, Gauge, ShieldCheck, FileWarning, ArrowLeft, Globe, Palette, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth, useTheme } from '../../App';
import { PageLayout } from '../../components/Sidebar';
import { ownerApi } from '../../lib/ownerApi';
import { DS_GROUPS, sectionsForGroup } from '../design-system/sections';

const NAV_ITEMS = [
  { to: '/owner/overview', label: 'Overview', icon: LayoutDashboard },
  { to: '/owner/accounts', label: 'Accounts', icon: Building2 },
  { to: '/owner/people', label: 'People & Users', icon: Users },
  { to: '/owner/website', label: 'Website', icon: Globe },
  { to: '/owner/design-system', label: 'Design System', icon: Palette },
  { to: '/owner/billing', label: 'Billing', icon: Receipt, disabled: true, hint: 'Phase 4' },
  { to: '/owner/usage', label: 'Usage', icon: Gauge, disabled: true, hint: 'Phase 4' },
  { to: '/owner/demo', label: 'Demo Accounts', icon: Beaker, disabled: true, hint: 'Phase 5' },
  { to: '/owner/access', label: 'Permissions', icon: ShieldCheck, disabled: true, hint: 'Phase 6' },
  { to: '/owner/audit', label: 'Audit & Cleanup', icon: FileWarning, disabled: true, hint: 'Phase 7' },
];

export default function OwnerConsoleLayout() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [meState, setMeState] = useState({ loading: true, isOwner: false });

  useEffect(() => {
    let cancelled = false;
    ownerApi.me()
      .then(d => { if (!cancelled) setMeState({ loading: false, isOwner: !!d?.is_orka_owner }); })
      .catch(() => { if (!cancelled) setMeState({ loading: false, isOwner: false }); });
    return () => { cancelled = true; };
  }, []);

  if (meState.loading) {
    return <div className="p-12 text-center text-stone-500" data-testid="owner-loading">Checking owner access…</div>;
  }
  if (!meState.isOwner) {
    return (
      <PageLayout title="Owner Console" user={user} onLogout={logout} theme={theme} setTheme={setTheme}>
        <div className="max-w-md mx-auto mt-24 p-8 rounded-2xl border border orka-border-subtle orka-surface-raised text-center" data-testid="owner-denied">
          <ShieldCheck className="w-10 h-10 mx-auto mb-3 text-stone-400" />
          <h2 className="text-lg font-semibold orka-text-primary">Owner Console restricted</h2>
          <p className="text-sm text-stone-500 mt-2">This area is reserved for Orka Owners. If you believe this is wrong, contact the platform owner.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-6 px-4 h-9 rounded-full bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm"
            data-testid="owner-denied-back"
          >
            Back to Dashboard
          </button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={
        <span className="flex items-center gap-2">
          <Crown className="w-5 h-5 text-amber-500" />
          Owner Console
        </span>
      }
      user={user}
      onLogout={logout}
      theme={theme}
      setTheme={setTheme}
    >
      {/* Internal banner — required by PRD §10.2 */}
      <div
        className="mb-4 rounded-xl border border-amber-300/60 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 dark:border-amber-700/40 px-4 py-2.5 flex items-center justify-between"
        data-testid="owner-internal-banner"
      >
        <div className="flex items-center gap-3">
          <Crown className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          <div>
            <div className="text-xs font-semibold tracking-wide uppercase text-amber-800 dark:text-amber-300">Internal Orka Layer</div>
            <div className="text-xs text-amber-700/80 dark:text-amber-200/70">You are acting as an Orka Owner. All actions are audited.</div>
          </div>
        </div>
        <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-amber-100/70 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border border-amber-200/60 dark:border-amber-700/40">
          {process.env.NODE_ENV === 'production' ? 'production' : 'development'}
        </span>
      </div>

      <div className="grid grid-cols-12 gap-6 min-h-[calc(100vh-12rem)]">
        {/* Sidebar nav */}
        <aside className="col-span-3 lg:col-span-2">
          <button
            onClick={() => navigate(-1)}
            className="mb-3 inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700"
            data-testid="owner-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          <nav className="space-y-1" data-testid="owner-nav">
            {NAV_ITEMS.map(item => {
              const Icon = item.icon;
              const active = location.pathname.startsWith(item.to);
              if (item.disabled) {
                return (
                  <div
                    key={item.to}
                    className="px-3 py-2 rounded-lg flex items-center justify-between text-stone-300 dark:text-slate-600 text-sm cursor-not-allowed"
                    title={item.hint}
                    data-testid={`owner-nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-disabled`}
                  >
                    <span className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      {item.label}
                    </span>
                    {item.hint && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full orka-surface-subtle text-stone-400">
                        {item.hint}
                      </span>
                    )}
                  </div>
                );
              }
              const isDesignSystem = item.to === '/owner/design-system';
              return (
                <div key={item.to}>
                  <NavLink
                    to={item.to}
                    className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors ${
                      active
                        ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 shadow-sm'
                        : 'orka-text-secondary hover:bg-stone-100 dark:hover:bg-slate-800'
                    }`}
                    data-testid={`owner-nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </NavLink>
                  {isDesignSystem && active && (
                    <DesignSystemSectionTree />
                  )}
                </div>
              );
            })}
          </nav>
        </aside>

        <main className="col-span-9 lg:col-span-10">
          <Outlet />
        </main>
      </div>
    </PageLayout>
  );
}

/**
 * DesignSystemSectionTree — collapsible sub-menu that appears beneath the
 * "Design System" nav item when that route is active. Each leaf deep-links
 * to `/owner/design-system#<section-id>` which auto-expands the target
 * section and smooth-scrolls it into view.
 */
function DesignSystemSectionTree() {
  const location = useLocation();
  const currentHash = location.hash?.replace('#', '') || '';
  const [openGroups, setOpenGroups] = useState(() => {
    // Default: expand all groups so the tree is browsable on first arrival
    const init = {};
    DS_GROUPS.forEach((g) => { init[g.key] = true; });
    return init;
  });

  const toggle = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div
      className="ml-4 mt-1 mb-2 pl-3 border-l orka-border-subtle space-y-0.5"
      data-testid="owner-nav-design-system-tree"
    >
      {DS_GROUPS.map((group) => {
        const sections = sectionsForGroup(group.key);
        const open = !!openGroups[group.key];
        return (
          <div key={group.key}>
            <button
              type="button"
              onClick={() => toggle(group.key)}
              className="w-full flex items-center justify-between gap-2 px-2 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider orka-text-tertiary hover:orka-text-secondary"
              aria-expanded={open}
              data-testid={`owner-nav-ds-group-${group.key}`}
            >
              <span className="flex items-center gap-1.5">
                {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                {group.label}
              </span>
              <span className="text-[9px] font-mono normal-case tracking-normal orka-text-tertiary/80">{sections.length}</span>
            </button>
            {open && (
              <ul className="mt-0.5 mb-1 space-y-0.5">
                {sections.map((s) => {
                  const active = currentHash === s.id;
                  return (
                    <li key={s.id}>
                      <a
                        href={`/owner/design-system#${s.id}`}
                        className={`block px-3 py-1 rounded-md text-[12px] orka-transition-colors ${
                          active
                            ? 'orka-bg-intent-soft orka-text-primary font-semibold'
                            : 'orka-text-secondary hover:bg-stone-100 dark:hover:bg-slate-800'
                        }`}
                        data-testid={`owner-nav-ds-section-${s.id}`}
                      >
                        {s.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
